import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState, useMemo } from 'react'
import envConfig from '@config/env.config'
import { Button } from '@shared/components/ui/button'
import { Info, Loader2, X, Check, ChevronDown, Trash2, Plus } from 'lucide-react'
import { CheckoutSuccessModal } from '../components/CheckoutSuccessModal'
import { PaymentLinkPreview } from '@features/payment-links/components/PaymentLinkPreview'
import { PreviewPlaceholder } from '@features/payment-links/components/preview/PreviewPlaceholder'
import { AutoSaveIndicator, DraftBadge } from '@features/payment-links/components/AutoSaveIndicator'
import { WizardPreviewPanel } from '@shared/components/ui/WizardPreviewPanel'
import { usePaymentLinkForm } from '@features/payment-links/hooks'
import { useAutoSave } from '@features/payment-links/hooks/useAutoSave'
import { ecommerceService } from '@shared/services/ecommerce.service'
import { toast } from 'sonner'
import type { PaymentLinkFormData } from '@shared/types/payment-links'
import { getInitialPaymentLinkFormData } from '@shared/types/payment-links'

// UI components
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@shared/components/ui/tabs'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { Textarea } from '@shared/components/ui/textarea'
import { Switch } from '@shared/components/ui/switch'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@shared/components/ui/select'
import { ImageUpload } from '@shared/components/ui/ImageUpload'
import { ALL_CURRENCIES } from '@shared/components/ui/CurrencySelector'
import { DatePicker } from '@shared/components/ui/date-picker'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@shared/components/ui/dialog'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@shared/components/ui/collapsible'
import { Checkbox } from '@shared/components/ui/checkbox'
import { Card, CardContent } from '@shared/components/ui/card'
import { cn } from '@shared/lib/utils'
// ─── Types ───────────────────────────────────────────────────────────────────

interface CheckoutExtras {
  websiteUrl: string
  productSource: 'vinr-platform' | 'direct-merchant'
  paymentMethods: string[]
  enableShippingOptions: boolean
  savePaymentDetails: boolean
  localizeByLocation: boolean
  allowCustomFields: boolean
  customFields: CustomField[]
}

interface CustomField {
  id: string
  type: 'text' | 'number' | 'dropdown'
  label: string
  isOptional: boolean
}

interface BrandingState {
  ctaText: 'pay' | 'checkout' | 'purchase'
  theme: string
  brand: string
  accent: string
  backgroundColor: string
  borderStyle: string
}

const INITIAL_EXTRAS: CheckoutExtras = {
  websiteUrl: '',
  productSource: 'vinr-platform',
  paymentMethods: ['visa', 'mastercard', 'apple-pay', 'google-pay', 'click-to-pay'],
  enableShippingOptions: false,
  savePaymentDetails: false,
  localizeByLocation: false,
  allowCustomFields: false,
  customFields: [],
}

const INITIAL_BRANDING: BrandingState = {
  ctaText: 'pay',
  theme: 'modern',
  brand: 'default',
  accent: 'default',
  backgroundColor: '#FFFFFF',
  borderStyle: 'rounded',
}

const PAYMENT_METHOD_OPTIONS = [
  { id: 'visa', label: 'Visa' },
  { id: 'mastercard', label: 'Mastercard' },
  { id: 'apple-pay', label: 'Apple Pay' },
  { id: 'google-pay', label: 'Google Pay' },
  { id: 'click-to-pay', label: 'Click to Pay' },
]

function InfoIcon({ title }: { title: string }) {
  return (
    <span title={title} className="inline-flex text-muted-foreground/60 hover:text-muted-foreground cursor-help">
      <Info className="h-3.5 w-3.5" />
    </span>
  )
}

// ─── Config builder ───────────────────────────────────────────────────────────

const ACCENT_COLORS: Record<string, string> = {
  default: '#0B3B45',
  blue: '#2563EB',
  green: '#16A34A',
  purple: '#9333EA',
}

function buildCheckoutConfig(formData: PaymentLinkFormData, branding: BrandingState, extras: CheckoutExtras) {
  return {
    name: formData.paymentPage.title || 'Checkout Page',
    accountId: envConfig.defaultAccountId,
    description: formData.paymentPage.description || '',
    email: '',
    primaryColor: ACCENT_COLORS[branding.accent] ?? '#0B3B45',
    backgroundColor: branding.backgroundColor || '#FFFFFF',
    borderStyle: (branding.borderStyle || 'rounded') as 'rounded' | 'square' | 'pill',
    allowBillingAddress: formData.requireShippingAddress ?? false,
    allowSaveCard: extras.savePaymentDetails ?? false,
    allowTaxSupport: formData.collectTax ?? false,
    taxPercentage: 0,
    allowContactInformation: formData.collectCustomerName ?? true,
    allowAddressAutocomplete: false,
    allowTermsAccepted: formData.requireTermsOfService ?? false,
    termsUrl: '',
    privacyUrl: '',
    allowShippingDetails: formData.requireShippingAddress && !formData.billingAddressOnly,
    allowQuantityChange: formData.allowQuantityChange ?? false,
    cartItems: null,
    productSource: extras.productSource,
    paymentMethods: extras.paymentMethods,
    enableShippingOptions: extras.enableShippingOptions,
    localizeByLocation: extras.localizeByLocation,
    websiteUrl: extras.websiteUrl,
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CheckoutConfigCreatePage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = !!id
  const [isLoadingData, setIsLoadingData] = useState(isEditMode)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [isDraft, setIsDraft] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Branding state
  const [branding, setBranding] = useState<BrandingState>(INITIAL_BRANDING)

  // Checkout extras state (ecommerce-specific fields not in PaymentLinkFormData)
  const [extras, setExtras] = useState<CheckoutExtras>(INITIAL_EXTRAS)

  // Payment link form state & hooks
  const {
    formData,
    isLoading,
    updateFormData,
    setFormData,
  } = usePaymentLinkForm()

  // Auto-save
  const autoSave = useAutoSave(formData, {
    enabled: !isEditMode || isDraft,
    draftId: isDraft ? id : undefined,
    onSaved: (_savedId) => {
    },
    onError: (error) => {
      console.error('Auto-save error:', error)
      toast.error('Failed to auto-save draft')
    }
  })

  // Local state for form UI
  const [paymentMethodsOpen, setPaymentMethodsOpen] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [showProductDialog, setShowProductDialog] = useState(false)
//  const [productSearchOpen, _setProductSearchOpen] = useState(false)
  const [showCustomMessaging, setShowCustomMessaging] = useState(false)
  const [newProduct, setNewProduct] = useState({ name: '', description: '', price: '', image: '' })

  const currencySymbol = useMemo(
    () => ALL_CURRENCIES.find(c => c.code === formData.currency)?.symbol ?? formData.currency,
    [formData.currency]
  )

  // Set default paymentFor on mount (required for preview)
  useEffect(() => {
    if (!formData.paymentFor) {
      updateFormData({ paymentFor: 'products-subscriptions' })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Load data in edit mode
  useEffect(() => {
    if (!isEditMode || !id) return
    setIsLoadingData(true)
    ecommerceService.getSingleCheckoutConfig(id)
      .then((loadedConfig) => {
        const initial = getInitialPaymentLinkFormData()
        setFormData({
          ...initial,
          paymentFor: 'products-subscriptions',
          paymentPage: {
            ...initial.paymentPage,
            title: loadedConfig.name || '',
            description: loadedConfig.description || '',
          },
          collectCustomerName: loadedConfig.allowContactInformation ?? true,
          requireShippingAddress: loadedConfig.allowShippingDetails ?? false,
          allowQuantityChange: loadedConfig.allowQuantityChange ?? false,
          collectTax: loadedConfig.allowTaxSupport ?? false,
          requireTermsOfService: loadedConfig.allowTermsAccepted ?? false,
          requirePhoneNumber: loadedConfig.allowPhoneNumber ?? false,
        })
        setIsDraft(loadedConfig.isDraft ?? false)
        setBranding(prev => ({
          ...prev,
          backgroundColor: loadedConfig.backgroundColor ?? '#FFFFFF',
          borderStyle: loadedConfig.borderStyle ?? 'rounded',
        }))
        setExtras(prev => ({
          ...prev,
          savePaymentDetails: loadedConfig.allowSaveCard ?? false,
        }))
      })
      .catch((err: Error) => {
        console.error('Failed to load checkout config:', err)
        toast.error('Failed to load checkout configuration')
        navigate('/ecommerce')
      })
      .finally(() => setIsLoadingData(false))
  }, [isEditMode, id, navigate, setFormData])

  const handleClose = () => navigate('/ecommerce')

  const handleSaveDraft = async () => {
    await autoSave.saveNow()
    toast.success('Draft saved successfully')
  }

  const handleCreate = async () => {
    if (!formData.paymentPage.title.trim()) {
      toast.error('Please enter a website name')
      return
    }

    setIsSaving(true)
    try {
      const config = buildCheckoutConfig(formData, branding, extras)
      if (isEditMode && id) {
        await ecommerceService.updateCheckoutConfig(id, config)
        toast.success('Checkout configuration updated successfully')
        navigate('/ecommerce')
      } else {
        await ecommerceService.createCheckoutConfig(config)
        setShowSuccessModal(true)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save configuration'
      toast.error(msg)
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddProduct = () => {
    if (!newProduct.name || !newProduct.price) return
    const product = {
      id: crypto.randomUUID(),
      name: newProduct.name,
      description: newProduct.description,
      price: parseFloat(newProduct.price) * 100,
      image: newProduct.image,
      quantity: 1,
      allowQuantityChange: false
    }
    updateFormData({ selectedProducts: [...formData.selectedProducts, product] })
    setNewProduct({ name: '', description: '', price: '', image: '' })
    setShowProductDialog(false)
  }

  const updateExtras = (updates: Partial<CheckoutExtras>) => {
    setExtras(prev => ({ ...prev, ...updates }))
  }

  if (isLoadingData) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col bg-neutral-50">
      {/* ── Header (same as Payment Link) ── */}
      <div className="flex h-20 items-center justify-between border-b bg-background px-6">
        <div className="flex items-center gap-5">
          <button
            onClick={handleClose}
            className="flex items-center justify-center text-foreground hover:text-foreground/80 transition-colors"
            aria-label="Go back"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-2.5">
            <span className="text-sm font-semibold text-foreground">
              {isEditMode ? 'Edit Checkout Page' : 'Create Checkout Page'}
            </span>
            {isDraft && <DraftBadge />}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <AutoSaveIndicator
            status={autoSave.status}
            lastSaved={autoSave.lastSaved}
          />
          <Button
            variant="outline"
            onClick={handleSaveDraft}
            disabled={isLoading || autoSave.isSaving}
            className="h-10 rounded-full px-4 text-sm font-medium"
          >
            Save as Draft
          </Button>
          <Button
            onClick={handleCreate}
            disabled={isLoading || isSaving}
            className="h-10 gap-1.5 rounded-full px-4 text-sm font-medium shadow-[0px_10px_28px_0px_rgba(11,59,69,0.1),0px_1px_2px_0px_rgba(11,59,69,0.06)]"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
              </>
            ) : (
              <>
                <span>{isEditMode ? 'Update' : 'Create'}</span>
                <Check className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* ── Main Content — Two Column Layout ── */}
      <div className="flex h-[calc(100vh-80px)] w-full items-start justify-between overflow-hidden">
        {/* Left Side — Form with 3 Tabs */}
        <div className="flex h-full w-[480px] shrink-0 flex-col pb-6 pl-6 pr-3 pt-6">
          <div className="flex h-full w-full flex-col overflow-y-auto rounded-[24px] bg-white px-7 pb-8 pt-6 shadow-[0px_10px_28px_0px_rgba(11,59,69,0.1),0px_1px_2px_0px_rgba(11,59,69,0.06)] scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400">

            {/* 3-Tab Navigation */}
            <Tabs defaultValue="payment">
              <TabsList className="w-full border border-border bg-muted/60 rounded-xl p-1 h-auto gap-0">
                <TabsTrigger
                  value="payment"
                  className="flex-1 rounded-lg py-1.5 text-sm border-b-0 -mb-0 data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
                >
                  Payment
                </TabsTrigger>
                <TabsTrigger
                  value="after-payment"
                  className="flex-1 rounded-lg py-1.5 text-sm border-b-0 -mb-0 data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
                >
                  After Payment
                </TabsTrigger>
                <TabsTrigger
                  value="style"
                  className="flex-1 rounded-lg py-1.5 text-sm border-b-0 -mb-0 data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
                >
                  Style
                </TabsTrigger>
              </TabsList>

              {/* ══════════════════════════════════════════════════════════ */}
              {/* TAB 1: Payment                                           */}
              {/* ══════════════════════════════════════════════════════════ */}
              <TabsContent value="payment" className="space-y-6 mt-4">

                {/* ── General ── */}
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-foreground">General</h3>

                  <div className="space-y-1.5">
                    <Label htmlFor="websiteName" className="text-sm font-medium">
                      Website name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="websiteName"
                      value={formData.paymentPage.title}
                      onChange={(e) => updateFormData({ paymentPage: { ...formData.paymentPage, title: e.target.value } })}
                      placeholder="Name of cause or service"
                      className="rounded-lg h-10"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="websiteUrl" className="text-sm font-medium">Website URL</Label>
                    <Input
                      id="websiteUrl"
                      value={extras.websiteUrl}
                      onChange={(e) => updateExtras({ websiteUrl: e.target.value })}
                      placeholder="https://yourstore.com"
                      className="rounded-lg h-10"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="description" className="text-sm font-medium">
                      Description <span className="text-muted-foreground">(optional)</span>
                    </Label>
                    <Textarea
                      id="description"
                      value={formData.paymentPage.description}
                      onChange={(e) => updateFormData({ paymentPage: { ...formData.paymentPage, description: e.target.value } })}
                      placeholder="More details about what the customers are paying for..."
                      className="rounded-lg min-h-[60px] resize-none"
                      rows={3}
                    />
                  </div>
                </div>

                {/* ── Configuration ── */}
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-foreground">Configuration</h3>

                  <div className="space-y-1.5">
                    <Label htmlFor="productSource" className="text-sm font-medium">Product source</Label>
                    <Select
                      value={extras.productSource}
                      onValueChange={(value: 'vinr-platform' | 'direct-merchant') => updateExtras({ productSource: value })}
                    >
                      <SelectTrigger className="rounded-lg h-9 bg-card">
                        <SelectValue placeholder="Select product source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vinr-platform">Products sourced from Vinr platform</SelectItem>
                        <SelectItem value="direct-merchant">Product sourced direct from merchant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Custom statement descriptor */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="statementDescriptor"
                        checked={formData.setCustomStatementDescriptor}
                        onCheckedChange={(checked) => updateFormData({ setCustomStatementDescriptor: checked, paymentDescriptor: checked ? formData.paymentDescriptor : '' })}
                      />
                      <Label htmlFor="statementDescriptor" className="text-sm font-medium cursor-pointer">
                        Set custom statement descriptor
                      </Label>
                    </div>
                    {formData.setCustomStatementDescriptor && (
                      <div className="ml-12 space-y-1">
                        <Input
                          value={formData.paymentDescriptor}
                          onChange={(e) => updateFormData({ paymentDescriptor: e.target.value })}
                          placeholder="Visible on customer statements"
                          className="rounded-lg h-10"
                          maxLength={22}
                        />
                        <p className="text-xs text-muted-foreground">Appears on customer's bank statement (max 22 characters)</p>
                      </div>
                    )}
                  </div>

                  {/* Allow quantity adjustment */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="allowQuantityChange"
                        checked={formData.allowQuantityChange}
                        onCheckedChange={(checked) => updateFormData({ allowQuantityChange: checked })}
                      />
                      <Label htmlFor="allowQuantityChange" className="text-sm font-medium cursor-pointer">
                        Allow customers to adjust product quantity
                      </Label>
                    </div>
                    {formData.allowQuantityChange && (
                      <div className="ml-12 flex items-center gap-2">
                        <span className="text-sm text-muted-foreground font-medium">Between</span>
                        <Input type="number" min="0" value={formData.quantityMin} onChange={(e) => updateFormData({ quantityMin: e.target.value })} className="h-10 w-14 text-sm text-center" />
                        <span className="text-sm text-muted-foreground font-medium">and</span>
                        <Input type="number" min="1" value={formData.quantityMax} onChange={(e) => updateFormData({ quantityMax: e.target.value })} className="h-10 w-14 text-sm text-center" />
                      </div>
                    )}
                  </div>

                  {/* Auto calculate tax */}
                  <div className="flex items-center gap-2">
                    <Switch
                      id="collectTax"
                      checked={formData.collectTax}
                      onCheckedChange={(checked) => updateFormData({ collectTax: checked })}
                    />
                    <Label htmlFor="collectTax" className="text-sm font-medium cursor-pointer">
                      Automatically calculate tax
                    </Label>
                  </div>

                  {/* Present shipping options */}
                  <div className="flex items-center gap-2">
                    <Switch
                      id="enableShippingOptions"
                      checked={extras.enableShippingOptions}
                      onCheckedChange={(checked) => updateExtras({ enableShippingOptions: checked })}
                    />
                    <Label htmlFor="enableShippingOptions" className="text-sm font-medium cursor-pointer">
                      Present shipping options
                    </Label>
                  </div>
                </div>

                {/* ── Collect from customers ── */}
                <div className="space-y-3">
                  <h3 className="text-base font-semibold text-foreground">Collect from customers</h3>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="collectCustomerName"
                      checked={formData.collectCustomerName}
                      onCheckedChange={(checked) => updateFormData({ collectCustomerName: checked })}
                    />
                    <Label htmlFor="collectCustomerName" className="text-sm font-medium cursor-pointer">
                      Customer names
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="collectBusinessName"
                      checked={formData.collectBusinessName}
                      onCheckedChange={(checked) => updateFormData({ collectBusinessName: checked })}
                    />
                    <Label htmlFor="collectBusinessName" className="text-sm font-medium cursor-pointer">
                      Business names
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="requirePhoneNumber"
                      checked={formData.requirePhoneNumber}
                      onCheckedChange={(checked) => updateFormData({ requirePhoneNumber: checked })}
                    />
                    <Label htmlFor="requirePhoneNumber" className="text-sm font-medium cursor-pointer">
                      Phone numbers
                    </Label>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="requireShippingAddress"
                        checked={formData.requireShippingAddress}
                        onCheckedChange={(checked) => updateFormData({ requireShippingAddress: checked })}
                      />
                      <Label htmlFor="requireShippingAddress" className="text-sm font-medium cursor-pointer">
                        Customer addresses
                      </Label>
                    </div>
                    {formData.requireShippingAddress && (
                      <div className="ml-12 space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="billingAddressScope"
                            checked={formData.billingAddressOnly}
                            onChange={() => updateFormData({ billingAddressOnly: true })}
                            className="h-3.5 w-3.5 text-primary"
                          />
                          <span className="text-sm font-medium">Billing address only</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="billingAddressScope"
                            checked={!formData.billingAddressOnly}
                            onChange={() => updateFormData({ billingAddressOnly: false })}
                            className="h-3.5 w-3.5 text-primary"
                          />
                          <span className="text-sm font-medium">Billing and shipping addresses</span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Payment Methods (collapsible) ── */}
                <Collapsible open={paymentMethodsOpen} onOpenChange={setPaymentMethodsOpen}>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between text-base font-semibold text-foreground hover:text-foreground/80 transition-colors"
                    >
                      <span>Payment Methods</span>
                      <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', paymentMethodsOpen && 'rotate-180')} />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-3 pt-4">
                    {PAYMENT_METHOD_OPTIONS.map((method) => (
                      <div key={method.id} className="flex items-center gap-2">
                        <Switch
                          id={`pm-${method.id}`}
                          checked={extras.paymentMethods.includes(method.id)}
                          onCheckedChange={(checked) => {
                            const methods = checked
                              ? [...extras.paymentMethods, method.id]
                              : extras.paymentMethods.filter(m => m !== method.id)
                            updateExtras({ paymentMethods: methods })
                          }}
                        />
                        <Label htmlFor={`pm-${method.id}`} className="text-sm font-medium cursor-pointer">
                          {method.label}
                        </Label>
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>

                {/* ── Advanced options ── */}
                <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between text-base font-semibold text-foreground hover:text-foreground/80 transition-colors"
                    >
                      <span>Advanced options</span>
                      <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', advancedOpen && 'rotate-180')} />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4 pt-4">
                    {/* Save payment details */}
                    <div className="flex items-center gap-2">
                      <Switch
                        id="savePaymentDetails"
                        checked={extras.savePaymentDetails}
                        onCheckedChange={(checked) => updateExtras({ savePaymentDetails: checked })}
                      />
                      <Label htmlFor="savePaymentDetails" className="text-sm font-medium cursor-pointer">
                        Save payment details for future use
                      </Label>
                    </div>

                    {/* Enable customer data saving (disabled) */}
                    <div className="flex items-center gap-2 opacity-50">
                      <Switch id="enableDataSaving" disabled />
                      <Label htmlFor="enableDataSaving" className="text-sm font-medium flex items-center gap-1.5">
                        Enable customer data saving
                        <InfoIcon title="This feature is not available yet" />
                      </Label>
                    </div>

                    {/* Allow business tax IDs (disabled) */}
                    <div className="flex items-center gap-2 opacity-50">
                      <Switch id="allowTaxIds" disabled />
                      <Label htmlFor="allowTaxIds" className="text-sm font-medium flex items-center gap-1.5">
                        Allow business customers to provide tax IDs
                        <InfoIcon title="Business customers can enter their tax ID for B2B purchases" />
                      </Label>
                    </div>

                    {/* Require terms */}
                    <div className="flex items-center gap-2">
                      <Switch
                        id="requireTermsOfService"
                        checked={formData.requireTermsOfService}
                        onCheckedChange={(checked) => updateFormData({ requireTermsOfService: checked })}
                      />
                      <Label htmlFor="requireTermsOfService" className="text-sm font-medium cursor-pointer">
                        Require customers to accept your terms
                      </Label>
                    </div>

                    {/* Allow coupon codes */}
                    <div className="flex items-center gap-2">
                      <Switch
                        id="allowPromotionCodes"
                        checked={formData.allowPromotionCodes}
                        onCheckedChange={(checked) => updateFormData({ allowPromotionCodes: checked })}
                      />
                      <Label htmlFor="allowPromotionCodes" className="text-sm font-medium cursor-pointer">
                        Allow coupon codes
                      </Label>
                    </div>

                    {/* Link expiration date */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="enableExpirationDate"
                          checked={formData.advanced.enableExpirationDate}
                          onCheckedChange={(checked) => updateFormData({ advanced: { ...formData.advanced, enableExpirationDate: checked } })}
                        />
                        <Label htmlFor="enableExpirationDate" className="text-sm font-medium cursor-pointer">
                          Set Link expiration date
                        </Label>
                      </div>
                      {formData.advanced.enableExpirationDate && (
                        <div className="ml-12">
                          <DatePicker
                            date={formData.advanced.expirationDate ? new Date(formData.advanced.expirationDate + 'T00:00:00') : undefined}
                            onSelect={(date) => updateFormData({ advanced: { ...formData.advanced, expirationDate: date ? date.toISOString().split('T')[0] : '' } })}
                            disabled={(date) => date < new Date(new Date().toDateString())}
                            placeholder="Select expiration date"
                            clearable
                            className="h-10 w-full"
                          />
                        </div>
                      )}
                    </div>

                    {/* Limit number of payments */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="limitLinkUsage"
                          checked={formData.advanced.limitLinkUsage}
                          onCheckedChange={(checked) => updateFormData({ advanced: { ...formData.advanced, limitLinkUsage: checked } })}
                        />
                        <Label htmlFor="limitLinkUsage" className="text-sm font-medium cursor-pointer">
                          Limit number of payments
                        </Label>
                      </div>
                      {formData.advanced.limitLinkUsage && (
                        <div className="ml-12 flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            value={formData.advanced.maxUsages ?? ''}
                            onChange={(e) => updateFormData({ advanced: { ...formData.advanced, maxUsages: e.target.value ? parseInt(e.target.value) : undefined } })}
                            className="h-10 w-24 text-sm"
                          />
                          <span className="text-sm font-medium text-foreground">Total payments</span>
                        </div>
                      )}
                    </div>

                    {/* Custom short code */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="useCustomShortCode"
                          checked={formData.useCustomShortCode}
                          onCheckedChange={(checked) => updateFormData({ useCustomShortCode: checked, shortCode: checked ? formData.shortCode : '' })}
                        />
                        <Label htmlFor="useCustomShortCode" className="text-sm font-medium cursor-pointer">
                          Set custom short code
                        </Label>
                      </div>
                      {formData.useCustomShortCode && (
                        <div className="ml-12 space-y-1">
                          <Input
                            value={formData.shortCode}
                            onChange={(e) => { const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''); updateFormData({ shortCode: value }) }}
                            placeholder="your-custom-link"
                            className="h-10 font-mono"
                          />
                          <p className="text-xs text-muted-foreground">Lowercase letters, numbers, and hyphens only. Min 3 characters.</p>
                        </div>
                      )}
                    </div>

                    {/* Localize pages based on customer location */}
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="localizeByLocation"
                        checked={extras.localizeByLocation}
                        onCheckedChange={(checked) => updateExtras({ localizeByLocation: !!checked })}
                      />
                      <div>
                        <Label htmlFor="localizeByLocation" className="cursor-pointer">Localize pages based on customer location</Label>
                        <p className="text-sm text-muted-foreground">Automatically adapt language and currency</p>
                      </div>
                    </div>

                    {/* Custom fields */}
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="allowCustomFields"
                        checked={extras.allowCustomFields}
                        onCheckedChange={(checked) => updateExtras({
                          allowCustomFields: !!checked,
                          customFields: checked ? extras.customFields : []
                        })}
                      />
                      <Label htmlFor="allowCustomFields" className="cursor-pointer">Allow custom fields</Label>
                    </div>

                    {extras.allowCustomFields && (
                      <div className="space-y-4 border rounded-lg p-4 bg-muted">
                        <Label className="text-sm font-medium">Custom Fields Configuration</Label>

                        {extras.customFields.map((field, index) => (
                          <Card key={field.id} className="bg-card">
                            <CardContent className="p-4 space-y-4">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Field {index + 1}</Label>
                                {extras.customFields.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      updateExtras({ customFields: extras.customFields.filter((_, i) => i !== index) })
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-xs text-muted-foreground">Field Type</Label>
                                  <Select
                                    value={field.type}
                                    onValueChange={(value: 'text' | 'number' | 'dropdown') => {
                                      const newFields = [...extras.customFields]
                                      newFields[index] = { ...field, type: value }
                                      updateExtras({ customFields: newFields })
                                    }}
                                  >
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="text">Text</SelectItem>
                                      <SelectItem value="number">Number</SelectItem>
                                      <SelectItem value="dropdown">Dropdown</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div>
                                  <Label className="text-xs text-muted-foreground">Label Name</Label>
                                  <Input
                                    value={field.label}
                                    onChange={(e) => {
                                      const newFields = [...extras.customFields]
                                      newFields[index] = { ...field, label: e.target.value }
                                      updateExtras({ customFields: newFields })
                                    }}
                                    placeholder="Enter field label"
                                  />
                                </div>
                              </div>

                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  checked={field.isOptional}
                                  onCheckedChange={(checked) => {
                                    const newFields = [...extras.customFields]
                                    newFields[index] = { ...field, isOptional: !!checked }
                                    updateExtras({ customFields: newFields })
                                  }}
                                />
                                <Label className="text-sm">Mark as optional</Label>
                              </div>
                            </CardContent>
                          </Card>
                        ))}

                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            const newField: CustomField = {
                              id: `field_${Date.now()}`,
                              type: 'text',
                              label: '',
                              isOptional: false
                            }
                            updateExtras({ customFields: [...extras.customFields, newField] })
                          }}
                          className="w-full"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add another field
                        </Button>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </TabsContent>

              {/* ══════════════════════════════════════════════════════════ */}
              {/* TAB 2: After Payment                                     */}
              {/* ══════════════════════════════════════════════════════════ */}
              <TabsContent value="after-payment" className="space-y-6 mt-4">
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-foreground">After payment</h3>

                  {/* Show confirmation page */}
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => updateFormData({ afterPaymentBehavior: 'confirmation' })}
                      className="flex items-center gap-3 text-left"
                    >
                      <span className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                        formData.afterPaymentBehavior === 'confirmation' ? 'border-primary' : 'border-muted-foreground/40'
                      )}>
                        {formData.afterPaymentBehavior === 'confirmation' && (
                          <span className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </span>
                      <span className="text-sm font-medium text-foreground">Show confirmation page</span>
                    </button>

                    {formData.afterPaymentBehavior === 'confirmation' && (
                      <div className="ml-4 space-y-4 pt-1">
                        <label className="flex cursor-pointer items-center gap-2.5">
                          <input
                            type="checkbox"
                            checked={showCustomMessaging}
                            onChange={(e) => setShowCustomMessaging(e.target.checked)}
                            className="h-4 w-4 rounded border-border text-primary accent-primary focus:ring-primary"
                          />
                          <span className="text-sm font-medium text-foreground">
                            Change success messaging{' '}
                            <span className="font-normal text-muted-foreground">(optional)</span>
                          </span>
                        </label>

                        {showCustomMessaging && (
                          <div className="space-y-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="afterPaymentTitle" className="text-sm font-medium text-foreground">Title</Label>
                              <Input
                                id="afterPaymentTitle"
                                value={formData.afterPayment.title}
                                onChange={(e) => updateFormData({ afterPayment: { ...formData.afterPayment, title: e.target.value } })}
                                placeholder="e.g., Thank you for your support!"
                                className="h-10"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="afterPaymentDescription" className="text-sm font-medium text-foreground">Message</Label>
                              <Textarea
                                id="afterPaymentDescription"
                                value={formData.afterPayment.description}
                                onChange={(e) => updateFormData({ afterPayment: { ...formData.afterPayment, description: e.target.value } })}
                                placeholder="e.g., Your payment has been received. Thank you!"
                                className="resize-none"
                                rows={2}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Redirect to your website */}
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => updateFormData({ afterPaymentBehavior: 'redirect' })}
                      className="flex items-center gap-3 text-left"
                    >
                      <span className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                        formData.afterPaymentBehavior === 'redirect' ? 'border-primary' : 'border-muted-foreground/40'
                      )}>
                        {formData.afterPaymentBehavior === 'redirect' && (
                          <span className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </span>
                      <span className="text-sm font-medium text-foreground">Redirect to your website</span>
                    </button>

                    {formData.afterPaymentBehavior === 'redirect' && (
                      <div className="ml-4 space-y-1.5 pt-1">
                        <Label htmlFor="redirectUrl" className="text-sm font-medium text-foreground">
                          Redirect URL <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="redirectUrl"
                          value={formData.redirectUrl || ''}
                          onChange={(e) => updateFormData({ redirectUrl: e.target.value })}
                          placeholder="https://yourwebsite.com/thank-you"
                          className="h-10"
                        />
                        <p className="text-xs text-muted-foreground">Payment details will be included as URL parameters.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Invoice & Receipts */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-foreground">Invoice & Receipts</h3>
                  <div className="flex items-start gap-3">
                    <Switch
                      id="enableInvoice"
                      checked={formData.afterPaymentBehavior === 'invoice'}
                      onCheckedChange={(checked) => updateFormData({ afterPaymentBehavior: checked ? 'invoice' : 'confirmation' })}
                    />
                    <div className="flex-1">
                      <Label htmlFor="enableInvoice" className="cursor-pointer text-sm font-medium text-foreground block mb-1">
                        Send invoice after payment
                      </Label>
                      <div className="space-y-1 text-xs text-muted-foreground">
                        <p>
                          VINR charges 0.4% of the transaction total, capped at €2.00 per invoice.{' '}
                          <a href="#" className="text-primary hover:underline" onClick={(e) => e.preventDefault()}>Learn more</a>
                        </p>
                        <p>
                          Receipts are free and sent automatically. You can customize settings in your{' '}
                          <a href="#" className="text-primary hover:underline" onClick={(e) => e.preventDefault()}>Email Settings</a>
                          {' '}and{' '}
                          <a href="#" className="text-primary hover:underline" onClick={(e) => e.preventDefault()}>Invoice Template</a>.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* ══════════════════════════════════════════════════════════ */}
              {/* TAB 3: Style                                             */}
              {/* ══════════════════════════════════════════════════════════ */}
              <TabsContent value="style" className="space-y-5 mt-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ctaText" className="text-sm font-medium">Call to action button</Label>
                  <Select
                    value={branding.ctaText}
                    onValueChange={(value: 'pay' | 'checkout' | 'purchase') => setBranding(prev => ({ ...prev, ctaText: value }))}
                  >
                    <SelectTrigger className="w-[180px] h-9 rounded-lg bg-card"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pay">Pay</SelectItem>
                      <SelectItem value="checkout">Checkout</SelectItem>
                      <SelectItem value="purchase">Purchase</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="theme" className="text-sm font-medium">Theme</Label>
                  <Select
                    value={branding.theme}
                    onValueChange={(value) => setBranding(prev => ({ ...prev, theme: value }))}
                  >
                    <SelectTrigger className="w-[229px] h-9 rounded-lg bg-card"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="modern">Modern</SelectItem>
                      <SelectItem value="classic">Classic</SelectItem>
                      <SelectItem value="minimal">Minimal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="brand" className="text-sm font-medium">Brand</Label>
                  <Select
                    value={branding.brand}
                    onValueChange={(value) => setBranding(prev => ({ ...prev, brand: value }))}
                  >
                    <SelectTrigger className="w-[229px] h-9 rounded-lg bg-card"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="accent" className="text-sm font-medium">Accent color</Label>
                  <Select
                    value={branding.accent}
                    onValueChange={(value) => setBranding(prev => ({ ...prev, accent: value }))}
                  >
                    <SelectTrigger className="w-[229px] h-9 rounded-lg bg-card"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default</SelectItem>
                      <SelectItem value="blue">Blue</SelectItem>
                      <SelectItem value="green">Green</SelectItem>
                      <SelectItem value="purple">Purple</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Website background</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={branding.backgroundColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, backgroundColor: e.target.value }))}
                      className="h-10 w-16 rounded-lg border border-input cursor-pointer"
                    />
                    <Input
                      value={branding.backgroundColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, backgroundColor: e.target.value }))}
                      placeholder="#FFFFFF"
                      className="w-36 rounded-lg h-10"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="borderStyle" className="text-sm font-medium">Style</Label>
                  <Select
                    value={branding.borderStyle}
                    onValueChange={(value) => setBranding(prev => ({ ...prev, borderStyle: value }))}
                  >
                    <SelectTrigger className="w-[229px] h-9 rounded-lg bg-card"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rounded">Rounded</SelectItem>
                      <SelectItem value="square">Square</SelectItem>
                      <SelectItem value="pill">Pill</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Right Panel — Preview */}
        <WizardPreviewPanel
          preview={(isMobileView) => formData.paymentFor
            ? <PaymentLinkPreview formData={formData} isMobileView={isMobileView} />
            : <PreviewPlaceholder step={1} paymentType={formData.paymentFor || undefined} />
          }
          infoText={
            <>
              You can configure{' '}
              <a href="/settings/payment-methods" className="text-[#2a9d90] font-medium hover:underline">payment methods</a>
              {' '}and{' '}
              <a href="/settings/branding" className="text-[#2a9d90] font-medium hover:underline">how page looks</a>
              {' '}in your Account settings.
            </>
          }
        />
      </div>

      {/* Add Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Add New Product</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="productName" className="text-sm font-semibold text-foreground">
                Product name <span className="text-destructive">*</span>
              </Label>
              <Input id="productName" value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} placeholder="Enter product name" className="h-10" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="productDescription" className="text-sm font-semibold text-foreground">
                Description <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Textarea id="productDescription" value={newProduct.description} onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })} placeholder="Product description" className="min-h-[80px] resize-none" rows={3} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="productPrice" className="text-sm font-semibold text-foreground">
                Price <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{currencySymbol}</span>
                <Input id="productPrice" type="number" step="0.01" min="0" value={newProduct.price} onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })} placeholder="0.00" className="h-10 pl-7" />
              </div>
            </div>
            <ImageUpload
              value={newProduct.image}
              onChange={(url) => setNewProduct({ ...newProduct, image: url })}
              onRemove={() => setNewProduct({ ...newProduct, image: '' })}
              label="Product image (optional)"
              description="PNG, JPG, GIF or WebP (max. 5MB)"
              aspectRatio="1/1"
              maxSizeMB={5}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setShowProductDialog(false); setNewProduct({ name: '', description: '', price: '', image: '' }) }}>Cancel</Button>
            <Button type="button" onClick={handleAddProduct} disabled={!newProduct.name || !newProduct.price}>Add Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success modal */}
      <CheckoutSuccessModal
        open={showSuccessModal}
        onOpenChange={(open) => {
          setShowSuccessModal(open)
          if (!open) navigate('/ecommerce')
        }}
        checkoutName={formData.paymentPage.title || 'Checkout Page'}
      />
    </div>
  )
}
